import { API_ENDPOINTS } from "../../enums/apiUrls";
import { logDataToConsole, logError } from "../../utils/customLogger";

/**
 * Singleton class that manages bearer token authentication.
 * Handles token fetching, caching, expiration, and automatic refresh.
 * Prevents multiple simultaneous token fetch requests by queuing them.
 *
 * @class BearerTokenManager
 * @example
 * ```typescript
 * const tokenManager = BearerTokenManager.getInstance();
 * const token = await tokenManager.fetchToken();
 * ```
 */
class BearerTokenManager {
  /** Singleton instance of the BearerTokenManager */
  private static instance: BearerTokenManager;

  /** The cached bearer token */
  private token: string | null = null;

  /** Token expiration time in milliseconds (Unix timestamp) */
  private expirationTime: number | null = null;

  /** Promise for ongoing token fetch to prevent duplicate requests */
  private fetchPromise: Promise<string | null> | null = null;

  /**
   * Private constructor to enforce singleton pattern.
   * Use getInstance() to get the instance.
   * @private
   */
  private constructor() {}

  /**
   * Gets the singleton instance of BearerTokenManager.
   * Creates a new instance if one doesn't exist.
   *
   * @static
   * @returns {BearerTokenManager} The singleton instance
   */
  public static getInstance(): BearerTokenManager {
    if (!BearerTokenManager.instance) {
      BearerTokenManager.instance = new BearerTokenManager();
    }
    return BearerTokenManager.instance;
  }

  /**
   * Gets the current bearer token if it's still valid.
   * Returns null if the token is expired or doesn't exist.
   *
   * @readonly
   * @returns {string | null} The valid bearer token or null
   */
  public get bearerToken(): string | null {
    if (
      this.token &&
      this.expirationTime &&
      this.expirationTime > new Date().getTime()
    ) {
      return this.token;
    }
    return null;
  }

  /**
   * Fetches a bearer token from the authentication API.
   * Returns cached token if still valid.
   * Prevents duplicate simultaneous requests by reusing the same promise.
   *
   * @async
   * @returns {Promise<string>} The bearer token
   * @throws {Error} If token fetch fails
   */
  public async fetchToken(): Promise<string | null> {
    // If there's already a valid token, return it
    if (
      this.token &&
      this.expirationTime &&
      this.expirationTime > new Date().getTime()
    ) {
      return this.token;
    }

    // If a fetch is already in progress, return that promise
    if (this.fetchPromise) {
      return this.fetchPromise as Promise<string | null>;
    }

    // Start a new fetch
    this.fetchPromise = this.performTokenFetch();
    const result = await this.fetchPromise;
    this.fetchPromise = null;
    return result as string | null;
  }

  /**
   * Performs the actual HTTP request to fetch a new bearer token.
   * Updates the cached token and expiration time on success.
   *
   * @private
   * @async
   * @returns {Promise<string | null>} The bearer token or null on failure
   */
  private async performTokenFetch(): Promise<string | null> {
    const url = API_ENDPOINTS.BEARER_TOKEN_URL;

    const requestBody = {
      username: process.env.REACT_APP_USERNAME,
      password: process.env.REACT_APP_PASSWORD,
    };

    try {
      const response = await fetch(url, {
        method: "POST",
        body: JSON.stringify(requestBody),
        headers: {
          "Content-Type": "application/json",
        },
      });
      const data = await response.json();
      logDataToConsole(data);

      this.token = data.access_token;
      this.expirationTime = new Date().getTime() + data.expiration_time * 1000;

      return this.token;
    } catch (error) {
      logError(error as Error, "Error while getting bearer token");
      return null;
    }
  }

  /**
   * Clears the cached token and expiration time.
   * Useful for logout or when forcing a token refresh.
   *
   * @returns {void}
   */
  public clearToken(): void {
    this.token = null;
    this.expirationTime = null;
  }
}

/**
 * Convenience function to get a bearer token.
 * Uses the singleton BearerTokenManager instance.
 *
 * @async
 * @returns {Promise<string>} The bearer token
 * @throws {Error} If token fetch fails
 *
 * @example
 * ```typescript
 * const token = await getBearerToken();
 * // Use token in API requests
 * ```
 */
export async function getBearerToken() {
  const tokenManager = BearerTokenManager.getInstance();
  return tokenManager.fetchToken();
}
